<template>
  <div>
    <b-modal v-model="show" size="lg" :title="mode === 'edit' ? 'Edit Employee' : 'Add Employee'" hide-footer @hide="onHide">
      <div class="company-client-employee-modal">
        <div class="company-client-employee-modal__intro">
          <div class="company-client-employee-modal__eyebrow">Company Client Employee</div>
          <div class="company-client-employee-modal__lede">
            {{
              mode === "edit"
                ? "Update employee identity, WPS, and banking information."
                : "Add a new employee to this company client directory."
            }}
          </div>
        </div>

        <b-form @submit.prevent="onSubmit">
          <section class="company-client-employee-modal__section">
            <div class="company-client-employee-modal__section-head">
              <h4>Personal</h4>
              <p>Basic identity and contact information.</p>
            </div>
            <div class="company-client-employee-modal__grid">
              <div class="ds-field company-client-employee-modal__field-full">
                <label class="ds-field__label">Full Name</label>
                <el-input v-model="form.full_name" size="small" placeholder="Input full name" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Birthday</label>
                <el-date-picker
                  :picker-options="birthdayPickerOptions"
                  class="w-100"
                  v-model="form.birthday"
                  type="date"
                  placeholder="Pick birthday"
                  format="dd MMMM yyyy"
                  value-format="yyyy-MM-dd"
                />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Gender</label>
                <el-select v-model="form.gender" size="small" placeholder="Select gender" filterable class="w-100">
                  <el-option v-for="item in genderOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Phone Number</label>
                <el-input v-model="form.phone_number" size="small" placeholder="Input phone number" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Email</label>
                <el-input v-model="form.email" size="small" placeholder="Input email" />
              </div>

              <div class="ds-field company-client-employee-modal__field-full">
                <label class="ds-field__label">Address</label>
                <el-input v-model="form.address" size="small" type="textarea" :rows="3" placeholder="Input address" />
              </div>
            </div>
          </section>

          <section class="company-client-employee-modal__section">
            <div class="company-client-employee-modal__section-head">
              <h4>Employment</h4>
              <p>WPS amount, employee access, and policy status.</p>
            </div>
            <div class="company-client-employee-modal__grid">
              <div class="ds-field">
                <label class="ds-field__label">User Status</label>
                <el-select v-model="form.user_status" size="small" placeholder="Select user status" filterable class="w-100">
                  <el-option v-for="item in statusOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Salary</label>
                <el-input v-model="form.salary" size="small" placeholder="Input salary" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Is Private</label>
                <el-select v-model="form.is_private" size="small" placeholder="Select privacy status" filterable class="w-100">
                  <el-option v-for="item in booleanOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Policy Agreement</label>
                <el-select
                  v-model="form.is_agree_policy"
                  size="small"
                  placeholder="Select policy agreement"
                  filterable
                  class="w-100"
                >
                  <el-option v-for="item in booleanOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field company-client-employee-modal__field-full">
                <label class="ds-field__label">Marital Status</label>
                <el-select
                  v-model="form.marital_status"
                  size="small"
                  placeholder="Select marital status"
                  filterable
                  class="w-100"
                >
                  <el-option v-for="item in maritalStatusOptions" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
              </div>
            </div>
          </section>

          <section class="company-client-employee-modal__section">
            <div class="company-client-employee-modal__section-head">
              <h4>Banking</h4>
              <p>Destination account for WPS disbursement.</p>
            </div>
            <div class="company-client-employee-modal__grid">
              <div class="ds-field">
                <label class="ds-field__label">Bank Name</label>
                <el-input v-model="form.bank_name" size="small" placeholder="Input bank name" />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Bank Account</label>
                <el-input v-model="form.bank_account" size="small" placeholder="Input bank account number" />
              </div>
            </div>
          </section>
        </b-form>

        <div class="company-client-employee-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button class="ds-btn ds-btn--primary ds-btn--sm" :disabled="loading.submit" @click="onSubmit">
            {{ mode === "edit" ? "Update Employee" : "Create Employee" }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import {
  EMPLOYEE_GENDER_OPTIONS,
  EMPLOYEE_MARITAL_STATUS_OPTIONS,
  EMPLOYEE_BOOLEAN_OPTIONS,
} from "@/library/employee-form-options";
import { EMPLOYEE_STATUS_OPTIONS } from "@/library/employee-status";
import popupErrorMessages from "@/library/popup-error-messages";
import {
  UPDATE_COMPANY_CLIENT_EMPLOYEE_RECORD,
  CREATE_COMPANY_CLIENT_EMPLOYEE_RECORD,
} from "../../../store/modules/company_clients";

const createDefaultForm = () => ({
  id: null,
  company_client_id: null,
  full_name: "",
  birthday: "",
  gender: "",
  religion: "",
  phone_number: "",
  email: "",
  bank_name: "",
  bank_account: "",
  salary: "",
  is_private: "",
  marital_status: "",
  is_agree_policy: "",
  address: "",
  user_status: "",
});

export default {
  name: "CreateCompanyClientEmployee",
  props: {
    isShown: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    clientData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      show: false,
      loading: {
        submit: false,
      },
      genderOptions: EMPLOYEE_GENDER_OPTIONS,
      maritalStatusOptions: EMPLOYEE_MARITAL_STATUS_OPTIONS,
      booleanOptions: EMPLOYEE_BOOLEAN_OPTIONS,
      statusOptions: EMPLOYEE_STATUS_OPTIONS,
      form: createDefaultForm(),
      birthdayPickerOptions: {
        disabledDate: (date) => moment(date).isAfter(moment(), "day"),
      },
    };
  },
  methods: {
    resetForm() {
      this.form = createDefaultForm();
    },
    hydrateForm(data = {}) {
      this.form = {
        ...createDefaultForm(),
        ...data,
        birthday: data?.birthday ? moment(data.birthday).format("YYYY-MM-DD") : "",
        salary: data?.salary ?? "",
      };
    },
    onHide() {
      this.resetForm();
      this.$emit("onHide");
    },
    buildRequestBody() {
      return {
        full_name: this.form.full_name,
        birthday: this.form.birthday || null,
        gender: this.form.gender,
        religion: this.form.religion || null,
        phone_number: this.form.phone_number || null,
        email: this.form.email || null,
        bank_name: this.form.bank_name,
        bank_account: this.form.bank_account,
        salary: this.form.salary,
        is_private: this.form.is_private,
        marital_status: this.form.marital_status !== "" ? this.form.marital_status : null,
        is_agree_policy: this.form.is_agree_policy,
        address: this.form.address || null,
        user_status: this.form.user_status,
      };
    },
    async onSubmit() {
      try {
        this.loading.submit = true;
        const requestBody = this.buildRequestBody();

        if (this.mode === "create") {
          await this.$store.dispatch(CREATE_COMPANY_CLIENT_EMPLOYEE_RECORD, {
            id: this.$route.params.id,
            ...requestBody,
          });

          this.$message({
            title: "Success",
            type: "success",
            message: "Employee successfully added",
          });
        } else {
          await this.$store.dispatch(UPDATE_COMPANY_CLIENT_EMPLOYEE_RECORD, {
            id: this.form.company_client_id || this.$route.params.id,
            employee_id: this.form.id,
            ...requestBody,
          });

          this.$message({
            title: "Success",
            type: "success",
            message: "Employee successfully updated",
          });
        }

        this.onHide();
      } catch (err) {
        popupErrorMessages(err?.response?.data || err);
      } finally {
        this.loading.submit = false;
      }
    },
  },
  watch: {
    isShown(val) {
      this.show = val;
      if (!val) {
        return;
      }

      if (this.mode === "edit" && this.clientData) {
        this.hydrateForm(this.clientData);
        return;
      }

      this.resetForm();
    },
  },
};
</script>

<style scoped>
.company-client-employee-modal__intro {
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(32, 107, 196, 0.08), rgba(32, 107, 196, 0.04));
}

.company-client-employee-modal__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #206bc4;
  margin-bottom: 6px;
}

.company-client-employee-modal__lede {
  font-size: 13px;
  color: #4b5563;
}

.company-client-employee-modal__section + .company-client-employee-modal__section {
  margin-top: 18px;
}

.company-client-employee-modal__section-head {
  margin-bottom: 12px;
}

.company-client-employee-modal__section-head h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #182433;
}

.company-client-employee-modal__section-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #656d77;
}

.company-client-employee-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.company-client-employee-modal__field-full {
  grid-column: 1 / -1;
}

.company-client-employee-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
}

@media (max-width: 768px) {
  .company-client-employee-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
